import { useRef, useEffect, useState, useMemo } from "react";
import { ref, getDownloadURL } from "firebase/storage";
import { storage } from "../../../config/firebase";

export interface UseVideoGridProps {
  moodImages: Array<{ label: string; enLabel: string; src?: string }>;
  videosReady: boolean;
  theme?: string;
  collection?: string;
}

export interface UseVideoGridReturn {
  videoRefs: React.MutableRefObject<(HTMLVideoElement | null)[]>;
  loadedVideos: Set<number>;
  loadedPlaceholders: Set<number>;
  videoErrors: Set<number>;
  moodToPathMap: Map<string, { placeholder: string; video: string }>;
  placeholdersPreloaded: boolean;
  handleVideoLoaded: (index: number) => Promise<void>;
  handleVideoError: (index: number) => void;
}

const SHARED_RAINFOREST_PLACEHOLDER_THEMES = new Set([
  "rainforest5",
  "rainforest7",
]);

/**
 * Custom hook for VideoGrid component
 * Handles video loading, error handling, and path mapping
 */
export function useVideoGrid({
  videosReady,
  theme,
  collection,
}: UseVideoGridProps): UseVideoGridReturn {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set());
  const [loadedPlaceholders, setLoadedPlaceholders] = useState<Set<number>>(
    new Set()
  );
  const [videoErrors, setVideoErrors] = useState<Set<number>>(new Set());
  const [videoPaths, setVideoPaths] = useState<string[]>([]);
  const [placeholderPaths, setPlaceholderPaths] = useState<string[]>([]);
  const retryAttempts = useRef<Record<number, number>>({});

  const themeKey = theme || "animals";
  const collectionKey = collection || "rainforest";
  const usesSharedRainforestPlaceholders =
    SHARED_RAINFOREST_PLACEHOLDER_THEMES.has(themeKey);

  useEffect(() => {
    let cancelled = false;
    const videoStoragePaths = [
      `${themeKey}/${collectionKey}/apathic.mp4`,
      `${themeKey}/${collectionKey}/anxious.mp4`,
      `${themeKey}/${collectionKey}/calm.mp4`,
      `${themeKey}/${collectionKey}/flow.mp4`,
    ];
    const placeholderStoragePaths = usesSharedRainforestPlaceholders
      ? [
          "animals/rainforest/apathic2.png",
          "animals/rainforest/anxious2.png",
          "animals/rainforest/calm2.png",
          "animals/rainforest/flow2.png",
        ]
      : [
          `${themeKey}/${collectionKey}/apathic.png`,
          `${themeKey}/${collectionKey}/anxious.png`,
          `${themeKey}/${collectionKey}/calm.png`,
          `${themeKey}/${collectionKey}/flow.png`,
        ];
    Promise.all([
      Promise.all(
        videoStoragePaths.map((path) => getDownloadURL(ref(storage, path)))
      ),
      Promise.all(
        placeholderStoragePaths.map((path) =>
          getDownloadURL(ref(storage, path))
        )
      ),
    ]).then(([videos, placeholders]) => {
      if (cancelled) return;
      setVideoPaths(videos);
      setPlaceholderPaths(placeholders);
    });
    return () => {
      cancelled = true;
    };
  }, [themeKey, collectionKey, usesSharedRainforestPlaceholders]);

  // Helper function to extract mood from filename
  const getMoodFromPath = (path: string) => {
    const filename = path.toLowerCase();
    if (filename.includes("apathic") || filename.includes("apt"))
      return "Apathy";
    if (filename.includes("anxious") || filename.includes("anx"))
      return "Anxiety";
    if (filename.includes("calm") || filename.includes("confident"))
      return "Confident";
    if (filename.includes("flow")) return "Flow";
    return null;
  };

  const moodToPathMap = useMemo(() => {
    const map = new Map<string, { placeholder: string; video: string }>();
    placeholderPaths.forEach((path, index) => {
      const mood = getMoodFromPath(path);
      if (mood) {
        map.set(mood, {
          placeholder: path,
          video: videoPaths[index] ?? "",
        });
      }
    });
    return map;
  }, [placeholderPaths, videoPaths]);

  useEffect(() => {
    placeholderPaths.forEach((path) => {
      if (!path) return;

      // Preload using link tag for better browser optimization
      try {
        const id = `preload-placeholder-${path}`;
        if (typeof document !== "undefined" && !document.getElementById(id)) {
          const link = document.createElement("link");
          link.rel = "preload";
          link.as = "image";
          link.href = path;
          link.id = id;
          document.head.appendChild(link);
        }
      } catch {
        /* noop */
      }

      // Also preload using Image object to ensure it's in cache
      const img = new Image();
      img.decoding = "sync";
      // @ts-ignore: fetchPriority not yet typed in TS lib
      img.fetchPriority = "high";
      img.src = path;
    });
  }, [placeholderPaths]);

  const handleVideoLoaded = async (index: number) => {
    const video = videoRefs.current[index];
    if (!video || loadedVideos.has(index)) return;

    setVideoErrors((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });

    const showVideo = () => {
      requestAnimationFrame(() => {
        setLoadedVideos((prev) => {
          if (prev.has(index)) return prev;
          return new Set([...prev, index]);
        });
      });
    };

    if (video.readyState >= 3) {
      showVideo();
    } else {
      const handleCanPlay = () => {
        showVideo();
        video.removeEventListener("canplaythrough", handleCanPlay);
      };
      video.addEventListener("canplaythrough", handleCanPlay, { once: true });
    }
    if (videosReady) {
      try {
        await video.play();
      } catch (error) {}
    }
  };

  const handleVideoError = (index: number) => {
    const video = videoRefs.current[index];
    if (!video) return;

    const attempts = retryAttempts.current[index] || 0;
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, attempts), 5000); // Exponential backoff, max 5s

    if (attempts < maxRetries) {
      retryAttempts.current[index] = attempts + 1;

      setTimeout(() => {
        video.load();
      }, retryDelay);
    } else {
      setVideoErrors((prev) => new Set([...prev, index]));
    }
  };

  useEffect(() => {
    if (!videosReady || videoPaths.length === 0) return;
    retryAttempts.current = {};
    videoRefs.current.forEach((video, index) => {
      if (video) {
        video.load();
        video.play().catch(() => {});
      }
    });
  }, [videosReady, videoPaths.length]);

  return {
    videoRefs,
    loadedVideos,
    loadedPlaceholders,
    videoErrors,
    moodToPathMap,
    placeholdersPreloaded: false,
    handleVideoLoaded,
    handleVideoError,
  };
}
